import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Heart, ArrowLeft, Shield, Lock, Eye, Database, UserCheck, Bell, Mail, FileText } from "lucide-react";

const LOGO_URL = "/images/singleseen_logo_transparent.png";

const PrivacyPolicyPage = () => {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 via-white to-amber-50/30">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-rose-100/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-36">
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <img src={LOGO_URL} alt="SingleSeen" className="h-36 w-auto" />
            </Link>
            <Link to="/" className="text-rose-600 hover:text-rose-700 flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-40 pb-16 bg-gradient-to-r from-rose-500 to-pink-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-6">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl text-white mb-4">{t('privacy.title')}</h1>
          <p className="text-rose-100 text-lg">{t('privacy.subtitle')}</p>
          <p className="text-rose-200 text-sm mt-4">{t('privacy.lastUpdated')}</p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-lg border border-rose-100 p-8 md:p-12 space-y-10">
            
            {/* Introduction */}
            <div className="prose prose-rose max-w-none">
              <p className="text-rose-700 text-lg leading-relaxed">
                At SingleSeen, we understand that privacy is paramount when it comes to finding your life partner. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform. 
                As a Muslim matrimonial service, we are committed to maintaining the highest standards of privacy and discretion.
              </p>
            </div>

            {/* Section 1 */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-rose-100 rounded-xl flex items-center justify-center">
                  <Database className="w-5 h-5 text-rose-500" />
                </div>
                <h2 className="font-serif text-xl text-rose-900">{t('privacy.section1Title')}</h2>
              </div>
              <div className="pl-13 space-y-4 text-rose-700">
                <h3 className="font-semibold text-rose-800">{t('privacy.section1PersonalTitle')}</h3>
                <ul className="list-disc pl-5 space-y-2">
                  <li>{t('privacy.section1Personal1')}</li>
                  <li>{t('privacy.section1Personal2')}</li>
                  <li>{t('privacy.section1Personal3')}</li>
                  <li>{t('privacy.section1Personal4')}</li>
                  <li>{t('privacy.section1Personal5')}</li>
                  <li>{t('privacy.section1Personal6')}</li>
                  <li>{t('privacy.section1Personal7')}</li>
                  <li>{t('privacy.section1Personal8')}</li>
                </ul>
                
                <h3 className="font-semibold text-rose-800 mt-6">{t('privacy.section1UsageTitle')}</h3>
                <ul className="list-disc pl-5 space-y-2">
                  <li>{t('privacy.section1Usage1')}</li>
                  <li>{t('privacy.section1Usage2')}</li>
                  <li>{t('privacy.section1Usage3')}</li>
                  <li>{t('privacy.section1Usage4')}</li>
                </ul>
              </div>
            </div>

            {/* Section 2 */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <Eye className="w-5 h-5 text-emerald-500" />
                </div>
                <h2 className="font-serif text-xl text-rose-900">{t('privacy.section2Title')}</h2>
              </div>
              <div className="pl-13 space-y-4 text-rose-700">
                <ul className="list-disc pl-5 space-y-2">
                  <li>{t('privacy.section2Item1')}</li>
                  <li>{t('privacy.section2Item2')}</li>
                  <li>{t('privacy.section2Item3')}</li>
                  <li>{t('privacy.section2Item4')}</li>
                  <li>{t('privacy.section2Item5')}</li>
                  <li>{t('privacy.section2Item6')}</li>
                </ul>
              </div>
            </div>

            {/* Section 3 */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Lock className="w-5 h-5 text-amber-600" />
                </div>
                <h2 className="font-serif text-xl text-rose-900">{t('privacy.section3Title')}</h2>
              </div>
              <div className="pl-13 space-y-4 text-rose-700">
                <p>{t('privacy.section3Intro')}</p>
                <ul className="list-disc pl-5 space-y-2">
                  <li>{t('privacy.section3Item1')}</li>
                  <li>{t('privacy.section3Item2')}</li>
                  <li>{t('privacy.section3Item3')}</li>
                  <li>{t('privacy.section3Item4')}</li>
                  <li>{t('privacy.section3Item5')}</li>
                </ul>
              </div>
            </div>

            {/* Section 4 */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                  <UserCheck className="w-5 h-5 text-purple-500" />
                </div>
                <h2 className="font-serif text-xl text-rose-900">{t('privacy.section4Title')}</h2>
              </div>
              <div className="pl-13 space-y-4 text-rose-700">
                <p>{t('privacy.section4Intro')}</p>
                <ul className="list-disc pl-5 space-y-2">
                  <li>{t('privacy.section4Item1')}</li>
                  <li>{t('privacy.section4Item2')}</li>
                  <li>{t('privacy.section4Item3')}</li>
                  <li>{t('privacy.section4Item4')}</li>
                </ul>
              </div>
            </div>

            {/* Section 5 */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Shield className="w-5 h-5 text-blue-500" />
                </div>
                <h2 className="font-serif text-xl text-rose-900">{t('privacy.section5Title')}</h2>
              </div>
              <div className="pl-13 space-y-4 text-rose-700">
                <p>{t('privacy.section5Intro')}</p>
                <ul className="list-disc pl-5 space-y-2">
                  <li>{t('privacy.section5Item1')}</li>
                  <li>{t('privacy.section5Item2')}</li>
                  <li>{t('privacy.section5Item3')}</li>
                  <li>{t('privacy.section5Item4')}</li>
                  <li>{t('privacy.section5Item5')}</li>
                </ul>
              </div>
            </div>

            {/* Section 6 */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-rose-100 rounded-xl flex items-center justify-center">
                  <Bell className="w-5 h-5 text-rose-500" />
                </div>
                <h2 className="font-serif text-xl text-rose-900">{t('privacy.section6Title')}</h2>
              </div>
              <div className="pl-13 space-y-4 text-rose-700">
                <p>{t('privacy.section6Intro')}</p>
                <ul className="list-disc pl-5 space-y-2">
                  <li>{t('privacy.section6Item1')}</li>
                  <li>{t('privacy.section6Item2')}</li>
                  <li>{t('privacy.section6Item3')}</li>
                  <li>{t('privacy.section6Item4')}</li>
                  <li>{t('privacy.section6Item5')}</li>
                  <li>{t('privacy.section6Item6')}</li>
                </ul>
              </div>
            </div>

            {/* Section 7 */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-5 h-5 text-emerald-500" />
                </div>
                <h2 className="font-serif text-xl text-rose-900">{t('privacy.section7Title')}</h2>
              </div>
              <div className="pl-13 space-y-4 text-rose-700">
                <p>{t('privacy.section7Intro')}</p>
                <ul className="list-disc pl-5 space-y-2">
                  <li>{t('privacy.section7Item1')}</li>
                  <li>{t('privacy.section7Item2')}</li>
                  <li>{t('privacy.section7Item3')}</li>
                  <li>{t('privacy.section7Item4')}</li>
                </ul>
                <p className="mt-4">{t('privacy.section7Note')}</p>
              </div>
            </div>

            {/* Section 8 */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Mail className="w-5 h-5 text-amber-600" />
                </div>
                <h2 className="font-serif text-xl text-rose-900">{t('privacy.section8Title')}</h2>
              </div>
              <div className="pl-13 space-y-4 text-rose-700">
                <p>{t('privacy.section8Intro')}</p>
                <div className="bg-rose-50 rounded-xl p-6 mt-4">
                  <p className="font-medium text-rose-900">{t('privacy.section8Team')}</p>
                  <p className="mt-2">Email: <a href="mailto:Matchmaker@singleseen.com" className="text-rose-500 hover:text-rose-600">Matchmaker@singleseen.com</a></p>
                  <p>{t('contact.locationValue')}</p>
                </div>
              </div>
            </div>

            {/* Updates Notice */}
            <div className="bg-gradient-to-r from-rose-50 to-pink-50 rounded-xl p-6 border border-rose-100">
              <h3 className="font-semibold text-rose-900 mb-2">{t('privacy.updatesTitle')}</h3>
              <p className="text-rose-700 text-sm">
                We may update this Privacy Policy from time to time. We will notify you of any material changes 
                by posting the new policy on this page and updating the "Last updated" date. We encourage you to 
                review this policy periodically.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-rose-900 text-rose-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-rose-400 to-pink-400 flex items-center justify-center">
                <Heart className="w-4 h-4 text-white fill-white" />
              </div>
              <span className="font-serif text-lg text-white">SingleSeen</span>
            </div>
            <div className="flex gap-8 text-sm">
              <Link to="/contact" className="hover:text-white transition-colors">{t('common.contactUs')}</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
              <Link to="/help" className="hover:text-white transition-colors">Help Center</Link>
            </div>
            <p className="text-sm">© 2024 SingleSeen. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
